import { T } from './Translate';
import React, { useState, useEffect, useContext } from "react";
import { FloatingMenuContext } from "../App";
import Box from '@mui/material/Box';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Add as AddIcon } from '../icons';
import { Link, useNavigate } from 'react-router-dom';

// Firebase imports
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getDatabase, ref, onValue, set, serverTimestamp } from 'firebase/database';

// Icon imports
import { Storefront as StorefrontIcon } from '../icons';
import { LocalOffer as OfferIcon } from '../icons';
import { Event as EventIcon } from '../icons';
import { DirectionsCar as CarIcon } from '../icons';
import { Flight as PropertyIcon } from '../icons';
import { useSubscriptionContext } from "./SubscriptionContext";
import { barColors } from "../theme";
import { isIOSApp } from "../utils/device";

const PLAN_LIMITS = {
  Free: { offers: 3, events: 2, trips: 1, cars: 1 },
  Básico: { offers: 3, events: 2, trips: 1, cars: 1 },
  Profesional: { offers: 10, events: 5, trips: 3, cars: 3 },
  Premium: { offers: Infinity, events: Infinity, trips: Infinity, cars: Infinity },
};

const PUBLICATION_TYPE_MAP = {
  "/publishOffer": "offers",
  "/publishEvents": "events",
  "/publishCar": "cars",
  "/publishProperty": "trips",
};

const ACTIONS = [
  { icon: <OfferIcon id="tour-etiqueta" />, name: "Ofertas", path: "/publishOffer" },
  { icon: <EventIcon id="tour-calendario" />, name: "Eventos", path: "/publishEvents" },
  { icon: <CarIcon id="tour-auto" />, name: "Autos", path: "/publishCar" },
  { icon: <PropertyIcon id="tour-avion" />, name: "Propiedades", path: "/publishProperty" },
];

// Botón "+" de publicar, embebido en la bottom nav de cada pantalla (antes
// flotaba sobre el contenido vía FloatingMenu.js). Sigue abriendo el menú de
// acciones hacia arriba (direction="up"), igual que antes.
//
// variant="sidebar": versión para la sidebar de escritorio
// (components/DesktopSidebar.js), un botón "Publicar" que abre las mismas
// acciones en un menú a su derecha; `collapsed` lo deja solo con el ícono.
const PublishSpeedDial = ({ variant, collapsed = false }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { speedDialOpen, setSpeedDialOpen } = useContext(FloatingMenuContext);
  const open = speedDialOpen;
  const setOpen = setSpeedDialOpen;
  const [openModal, setOpenModal] = useState(false);
  const [publicationCounts, setPublicationCounts] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const navigate = useNavigate();
  const auth = firebase.auth();
  const db = getDatabase();
  const { subscription, loading, isSubscriptionActive, isFreePlanExpired } = useSubscriptionContext();

  const currentPlan = subscription?.planName || null;

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || loading) return;

    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(db, `publicationCounts/${user.uid}/${monthYear}`);

    try {
      const unsubscribe = onValue(countsRef, (snapshot) => {
        if (snapshot.exists()) {
          setPublicationCounts(snapshot.val());
        } else {
          const initialCounts = {
            offers: 0,
            events: 0,
            trips: 0,
            cars: 0,
            timestamp: serverTimestamp(),
          };
          set(countsRef, initialCounts);
          setPublicationCounts(initialCounts);
        }
      }, (error) => {
        console.error("Error loading publication counts:", error);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error setting up publication counts listener:", error);
    }
  }, [auth.currentUser, db, loading]);

  const checkPublicationLimit = (path) => {
    if (!isSubscriptionActive) return false;

    const type = PUBLICATION_TYPE_MAP[path];
    const currentCount = publicationCounts[type] || 0;
    const limit = PLAN_LIMITS[currentPlan]?.[type] ?? 0;
    return currentCount < limit;
  };

  const incrementPublicationCount = async (type) => {
    const user = auth.currentUser;
    if (!user) return;

    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(db, `publicationCounts/${user.uid}/${monthYear}`);

    const newCounts = {
      ...publicationCounts,
      [type]: (publicationCounts[type] || 0) + 1,
      timestamp: serverTimestamp(),
    };

    await set(countsRef, newCounts);
  };

  const handlePublishClick = async (path) => {
    if (!isSubscriptionActive) {
      setOpenModal(true);
      return;
    }

    if (!checkPublicationLimit(path)) {
      setAlertMessage(
        <T>Has alcanzado el límite de publicaciones mensuales para tu plan.</T>
      );
      setShowAlert(true);
      return;
    }

    try {
      await incrementPublicationCount(PUBLICATION_TYPE_MAP[path]);
      navigate(path);
    } catch (error) {
      console.error("Error incrementing publication count:", error);
      setAlertMessage(<T>Error al actualizar el contador de publicaciones</T>);
      setShowAlert(true);
    }
  };

  const getModalMessage = () => {
    if (isFreePlanExpired) {
      return "Tu mes gratis terminó. Elegí un plan para seguir publicando.";
    }

    if (!subscription?.status) {
      return "Para publicar contenido, necesitas tener una suscripción activa. ¿Te gustaría suscribirte ahora?";
    }

    switch (subscription.status) {
      case "EXPIRED":
        return "Tu suscripción ha expirado por falta de pago. Por favor, renueva tu suscripción para continuar publicando.";
      case "CANCELLED":
        return "Tu suscripción ha sido cancelada. ¿Te gustaría reactivar tu suscripción?";
      case "NONE":
        return "Para publicar contenido, necesitas tener una suscripción activa. ¿Te gustaría suscribirte ahora?";
      default:
        return "Tu suscripción no está activa. ¿Te gustaría activar una suscripción?";
    }
  };

  const getModalTitle = () => {
    if (isFreePlanExpired) {
      return "Tu mes gratis terminó";
    }

    if (!subscription?.status) {
      return "Suscripción Requerida";
    }

    switch (subscription.status) {
      case "EXPIRED":
        return "Suscripción Expirada";
      case "CANCELLED":
        return "Suscripción Cancelada";
      case "NONE":
        return "Suscripción Requerida";
      default:
        return "Suscripción No Activa";
    }
  };

  if (loading) {
    return null;
  }

  const openStores = () => {
    setOpen(false);
    if (!isSubscriptionActive) {
      setOpenModal(true);
    } else {
      navigate("/stores");
    }
  };

  const trigger = variant === "sidebar" ? (
    <>
      {collapsed ? (
        <Tooltip title={<T>Publicar</T>} placement="right">
          <IconButton
            id="tour-publicar"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: barColors.icon,
              color: barColors.background,
              '&:hover': { backgroundColor: barColors.icon, opacity: 0.9 },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          id="tour-publicar"
          fullWidth
          startIcon={<AddIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            borderRadius: 999,
            py: 1.1,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            backgroundColor: barColors.icon,
            color: barColors.background,
            '&:hover': { backgroundColor: barColors.icon, opacity: 0.9 },
          }}
        >
          <T>Publicar</T>
        </Button>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{ sx: { ml: 1, minWidth: 200 } }}
      >
        {ACTIONS.map((action) => (
          <MenuItem
            key={action.name}
            onClick={() => {
              setMenuAnchor(null);
              handlePublishClick(action.path);
            }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText><T>{action.name}</T></ListItemText>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            openStores();
          }}
        >
          <ListItemIcon><StorefrontIcon /></ListItemIcon>
          <ListItemText><T>Tiendas</T></ListItemText>
        </MenuItem>
      </Menu>
    </>
  ) : (
      <Box sx={{ position: "relative", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SpeedDial
          ariaLabel="Menu de publicación"
          sx={{
            position: "absolute",
            bottom: 0,
            "& .MuiSpeedDial-fab": { width: 40, height: 40, minHeight: 40 },
            "& .MuiSpeedDial-actions": { gap: 0 },
            "& .MuiSpeedDialAction-fab": { margin: "4px 0" },
          }}
          icon={<SpeedDialIcon />}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          open={open}
          direction="up"
          FabProps={{
            id: "tour-publicar",
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              color: barColors.icon,
              '&:hover': {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }
          }}
        >
          {ACTIONS.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => handlePublishClick(action.path)}
            />
          ))}
          <SpeedDialAction
            key="tiendas"
            icon={<StorefrontIcon />}
            tooltipTitle="Tiendas"
            onClick={openStores}
          />
        </SpeedDial>
      </Box>
  );

  return (
    <>
      {trigger}

      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle><T>{getModalTitle()}</T></DialogTitle>
        <DialogContent>
          <Typography><T>{getModalMessage()}</T></Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}><T>Cancelar</T></Button>
          {!isIOSApp() && (
          <Button
            component={Link}
            to="/subscription"
            onClick={() => setOpenModal(false)}
            color="primary"
            variant="contained"
          >
            {subscription?.status === "EXPIRED"
              ? <T>Renovar Suscripción</T>
              : <T>Ir a Suscripciones</T>}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "160px" }}
      >
        <Alert
          onClose={() => setShowAlert(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PublishSpeedDial;
